import { AccordionTabChangeEvent } from 'primereact/accordion';
import { useEffect, useState } from 'react';

type AccordionStateConfig = {
  storageKey: string;
  userKey: string;
  defaultValue: number | number[] | null;
  multiple?: boolean;
  countTracking?: {
    currentCount: number;
    activeTab?: string;
    targetTab?: string;
    onAutoExpand?: () => void;
  };
};

type AccordionState = {
  activeIndex: number | number[] | null;
  count?: number;
};

export const useAccordionState = (config: AccordionStateConfig) => {
  const {
    storageKey,
    userKey,
    defaultValue,
    multiple = false,
    countTracking,
  } = config;

  const getStoredData = (): Record<string, AccordionState> => {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return {};

    const parsed = JSON.parse(stored);

    Object.keys(parsed).forEach((key) => {
      if (
        Array.isArray(parsed[key]) ||
        typeof parsed[key] === 'number' ||
        parsed[key] === null
      ) {
        parsed[key] = { activeIndex: parsed[key] };
      }
    });

    return parsed;
  };

  const storedData = getStoredData();
  const preselectedActiveIndex =
    userKey in storedData ? storedData[userKey].activeIndex : defaultValue;

  const [activeIndex, setActiveIndex] = useState<number | number[] | null>(
    preselectedActiveIndex,
  );

  const saveToStorage = (updates: Partial<AccordionState>) => {
    const existing = getStoredData();
    const current = existing[userKey] || {};

    localStorage.setItem(
      storageKey,
      JSON.stringify({
        ...existing,
        [userKey]: { ...current, ...updates },
      }),
    );
  };

  useEffect(() => {
    if (!countTracking) return;
    const { currentCount, activeTab, targetTab, onAutoExpand } = countTracking;

    if (!targetTab || activeTab === targetTab) {
      if (currentCount > 0) {
        const existing = getStoredData();
        const current = existing[userKey];
        const preservedCount = current?.count || 0;

        if (currentCount > preservedCount) {
          const expandedValue = multiple ? [0] : 0;
          setActiveIndex(expandedValue);
          saveToStorage({ activeIndex: expandedValue, count: currentCount });
          onAutoExpand?.();
        } else {
          saveToStorage({ count: currentCount });
        }
      }
    }
  }, [
    countTracking?.currentCount,
    countTracking?.activeTab,
    countTracking?.targetTab,
    userKey,
    multiple,
  ]);

  const handleActiveIndexChange = (e: AccordionTabChangeEvent) => {
    const newIndex = e.index as number | number[] | null;
    setActiveIndex(newIndex);
    saveToStorage({ activeIndex: newIndex });
  };

  const handleActiveIndexChangeByNumber = (index: number | number[] | null) => {
    setActiveIndex(index);
    saveToStorage({ activeIndex: index });
  };

  return {
    activeIndex,
    handleActiveIndexChange,
    handleActiveIndexChangeByNumber,
    saveToStorage,
  };
};
